import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import { v4 as uuidv4 } from 'uuid';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { TimeDurationFormat, formatDuration } from '../../../../../utils/time';
import { DateFormat } from '../../../../../utils/date';
import WorkoutFeedView from '../../../../Model/WorkoutFeedView';
import LoadingPage from '../../../../../components/LoadingPage';
import ActivityList from '../ActivityListView';

import texts from './texts';

const ACTIVITIES_PER_PAGE = 10;

const WorkoutHistory = ({ user, coachId }) => {
  const [workoutsCollection, setWorkoutsCollection] = useState({});
  const [limit, setLimit] = useState(ACTIVITIES_PER_PAGE);
  const [isReady, setIsReady] = useState(false);

  const isComponentMountedRef = useComponentMounted();

  const canLoadMore = useMemo(() => workoutsCollection?.docs?.length === limit, [limit, workoutsCollection]);

  useEffect(() => {
    const loadworkoutHistoryCollection = async () => {
      const workoutHistoryCollection = await WorkoutFeedView.getCompletedWorkoutHistoryByUser(
        user,
        coachId,
        limit,
      );
      if (isComponentMountedRef.current) {
        setWorkoutsCollection(workoutHistoryCollection);
        setIsReady(true);
      }
    };
    loadworkoutHistoryCollection();
  }, [
    user,
    coachId,
    isComponentMountedRef,
    limit,
  ]);

  const activities = useMemo(() => {
    if (workoutsCollection && workoutsCollection.docs) {
      return workoutsCollection.docs.map((historyItem) => ({
        key: `${historyItem.title}-${uuidv4()}`,
        tag: moment(historyItem.startDate).format(DateFormat.DATE_FORMAT_COMMA),
        content:
          format(texts.completedWorkoutText, {
            type: texts.types[historyItem.type],
            title: historyItem.title,
          })
          + (historyItem.duration
            ? format(texts.duration, {
              duration: formatDuration(
                historyItem.duration,
                TimeDurationFormat.CLOCK_FORMAT,
              ),
            })
            : ''),
        subContent: historyItem.comment
          ? format(texts.comment, { comment: historyItem.comment })
          : null,
      }));
    }
    return [];
  }, [workoutsCollection]);

  const handleLoadMore = useCallback(() => {
    setLimit((prevLimit) => prevLimit + ACTIVITIES_PER_PAGE);
  }, []);

  if (!isReady) {
    return <LoadingPage />;
  }

  return (
    <ActivityList
      activities={activities}
      canLoadMore={canLoadMore}
      handleLoadMore={handleLoadMore}
      emptyText={texts.emptyText}
    />
  );
};

WorkoutHistory.propTypes = {
  user: PropTypes.string.isRequired,
  coachId: PropTypes.string.isRequired,
};

export default WorkoutHistory;
